import * as React from "react";
import { Button, Gapped, Modal } from "react-ui";
import { useDispatch } from "react-redux";
import { useCallback, useEffect } from "react";

import { hideModalActionCreator } from "../../FormModalAction";
import { CustomFile } from "../../../../../Helpers/file";
import { ModalStateInfo } from "../../IModalState";
import { ServerErrorResponse } from "../../../../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";
import { isNotNullOrEmpty, noop, reject } from "../../../../../../../Common/TypingUtils";
import { useMetrics } from "../../../../../Controls/Internals/Metrics/MetricsProviderContext";
import {
    PATCH_FUF_MODAL_OPEN,
    PATCH_FUF_MODAL_TRANSFER_TYPE,
    XML_UPLOAD_ADD_MORE_FILES_CLICK,
    XML_UPLOAD_CLICK_CLOSE,
} from "../../../../../Controls/metricsAction";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { StepPage } from "../Utils/Styles/Modals.styles";
import { UploadedFileInfo } from "../../../../ActionCreators/FufUploadActionCreator";
import { runFloatingAsyncAction } from "../../../../../Helpers/TypingUtils";
import { useUploadFile } from "../Utils/UploadFileRedisign/UseUploadFile";
import { getAcceptFilesError, invalidFileErrorText } from "../Utils/TextContent";
import { delay } from "../../../../utils";
import { ModalLoader } from "../Utils/ModalLoader/ModalLoader";
import {
    extractFileExtension,
    FileProps,
    FilePropsWithTypeId,
    PatchFufSteps,
    TypeFileUploadErrors,
    UploadFileStatuses,
} from "../Utils/Types";
import { FileException, Underline } from "../Utils/UploadFileRedisign/UploadFile.styles";
import { UploadFileException } from "../Utils/UploadFileRedisign/UploadFileException";
import { useFormObject } from "../../../../../Controls/FormObjectProvider";

import { MergeNotice } from "./MergeNotice/MergeNotice";
import { CheckPropertyErrors, ICombineByTransfers, ICombineByTransfersSort } from "./Types";

export interface UploadSectionModalProps {
    modalSettings?: ModalStateInfo;
    currentGfv: string;
    getFileProperties: (file: CustomFile | undefined) => Promise<UploadedFileInfo>;
    combineMultipleWithTransfer: (
        files: ICombineByTransfers[],
        abortSignal?: AbortSignal
    ) => Promise<ServerErrorResponse | undefined>;
    reload: () => Promise<void>;
}

export const UploadSection = ({
    modalSettings,
    currentGfv,
    getFileProperties,
    combineMultipleWithTransfer,
    reload,
}: UploadSectionModalProps) => {
    const dispatch = useDispatch();
    const track = useMetrics();
    const form = useFormObject();
    const abortController = React.useRef(new AbortController());

    const [loading, setLoading] = React.useState(false);
    const [step, setStep] = React.useState<PatchFufSteps>(PatchFufSteps.SelectFiles);
    const [modalHeader, setModalHeader] = React.useState<React.ReactNode>(
        <>Загрузка файл{modalSettings?.multiple ? "ов" : "а"}</>
    );
    const [modalErrorBody, setModalErrorBody] = React.useState<string>("");

    const memoizedFilter = useCallback(filterValidFiles, []);
    const memoizedCheckValidFile = useCallback(checkValidFile, []);

    const { filesContainer, uploadedFiles, validDriveURLs, addUserFilesToDrive } =
        useUploadFile<ICombineByTransfersSort>({
            filterValidDriveUrl: memoizedFilter,
            checkValidFile: memoizedCheckValidFile,
            multiple: modalSettings?.multiple ?? true,
        });
    function filterValidFiles(fileToRemove: FileProps, url: ICombineByTransfersSort) {
        return url.id !== fileToRemove.id;
    }

    useEffect(() => {
        track(PATCH_FUF_MODAL_OPEN, {
            modalName: modalSettings?.modalName,
            acceptFiles: modalSettings?.acceptFiles.toString(),
        });
    }, []);

    const onClose = () => {
        abortController.current.abort();
        track(XML_UPLOAD_CLICK_CLOSE, { path: modalSettings?.pathToTable });
        dispatch(hideModalActionCreator());
    };

    useAsyncEffect(async () => {
        if (modalSettings?.currentInstancesCount) {
            setStep(PatchFufSteps.ConfirmMerge);
        } else {
            await addUserFilesToDrive(modalSettings?.multiple);
        }

        await form.changeSet.send();
    }, []);

    const addMoreUsersFilesToDrive = async (): Promise<void> => {
        track(XML_UPLOAD_ADD_MORE_FILES_CLICK, { path: modalSettings?.pathToTable });
        await addUserFilesToDrive(modalSettings?.multiple);
    };

    async function checkValidFile(
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<ICombineByTransfersSort[]>>
    ): Promise<FilePropsWithTypeId> {
        let errorMessageText: string = "";
        if (!modalSettings?.acceptFiles?.includes(extractFileExtension(file.uploadFile.name))) {
            errorMessageText = getAcceptFilesError(modalSettings?.acceptFiles);
        }

        const fileState: FilePropsWithTypeId = {
            id: file.id,
            uploadFile: file.uploadFile,
            status: UploadFileStatuses.error,
            driveUri: file.driveUri,
            errorMessage: errorMessageText ? <FileException>{errorMessageText}</FileException> : undefined,
        };

        modalSettings?.getTransferIdByProperties ??
            reject(
                `getTransferIdByProperties is not defined. Check it in openModalForUploadSection.js in ${currentGfv} helpers`
            );

        const driveUriGuard = file.driveUri;

        if (file.id && driveUriGuard && !errorMessageText) {
            const properties: UploadedFileInfo = await getFileProperties(file.uploadFile);

            if (!properties?.fileProperties) {
                if (properties.errorCode === TypeFileUploadErrors.invalidXml) {
                    fileState.errorMessage = <FileException>{invalidFileErrorText}</FileException>;
                    fileState.status = UploadFileStatuses.error;
                    return fileState;
                }
                if (properties.errorCode === TypeFileUploadErrors.fufSizeTooLarge) {
                    fileState.errorMessage = <FileException>{properties.message}</FileException>;
                    fileState.status = UploadFileStatuses.error;
                    return fileState;
                }
            }

            const sectionInfo = modalSettings?.sectionInfo;
            const transferType = modalSettings?.getTransferIdByProperties?.(
                properties.fileProperties,
                sectionInfo?.gfv
            );

            track(PATCH_FUF_MODAL_TRANSFER_TYPE, {
                uploadSection: sectionInfo?.gfv,
                transferType: transferType,
            });

            if (transferType === CheckPropertyErrors.NotSectionFound) {
                // setInvalidDriveURLs(prev => [...prev, file.driveUri ?? ""]);
                fileState.errorMessage = sectionInfo?.fileUploadNotSectionFoundErrorMessage ? (
                    <UploadFileException errorMessage={sectionInfo?.fileUploadNotSectionFoundErrorMessage} />
                ) : (
                    <FileException>
                        В файле нет данных по подразделу <Underline>{sectionInfo?.name}</Underline>
                    </FileException>
                );
            } else if (transferType === CheckPropertyErrors.UnknownFormat) {
                fileState.errorMessage = sectionInfo?.fileUploadUnknownFormatErrorMessage ? (
                    <UploadFileException errorMessage={sectionInfo?.fileUploadUnknownFormatErrorMessage} />
                ) : (
                    <FileException>
                        Неподходящее содержимое файла.
                        <br />
                        Загрузите отчет в формате <Underline>{sectionInfo?.name}</Underline>
                    </FileException>
                );
            } else if (properties.fileProperties?.IsLastVersion === "false") {
                fileState.errorMessage = (
                    <FileException>
                        Формат файла не поддерживается.
                        <br />
                        Загрузите отчет в новом формате.
                    </FileException>
                );
            } else {
                const url: ICombineByTransfersSort = {
                    id: file.id,
                    fufDriveUri: driveUriGuard,
                    typeId: transferType ?? "",
                };
                setValidDriveURLs(value => [...value, url]);
                fileState.typeId = transferType;
            }
        }
        fileState.status = fileState.errorMessage ? UploadFileStatuses.error : UploadFileStatuses.success;
        return fileState;
    }

    const uploadFilesToPatchFuf = async () => {
        setLoading(true);
        abortController.current = new AbortController();

        const filteredTransfers: Omit<ICombineByTransfers[], "id"> = validDriveURLs.map(url => ({
            fufDriveUri: url.fufDriveUri,
            typeId: url.typeId,
        }));

        if (filteredTransfers.length) {
            const response = await combineMultipleWithTransfer(filteredTransfers, abortController.current.signal);
            if (abortController.current.signal.aborted) {
                noop();
            } else if (response?.serverError) {
                setErrorStep(
                    "Не удалось загрузить файлы",
                    "Повторите попытку через несколько минут. Если ошибка повторяется, напишите в техподдержку."
                );
            } else {
                await reload();
                await delay(3000); // чтобы рывков на интерфейсе при перезагрузке не было
                onClose();
            }
        }
        setLoading(false);
    };

    const getDriveUrls = () =>
        uploadedFiles
            .map(file => (file.status !== UploadFileStatuses.error ? file.driveUri : undefined))
            .filter(isNotNullOrEmpty);

    const setErrorStep = (errorHeader: string, errorBody: string) => {
        setStep(PatchFufSteps.ErrorMessage);
        setModalHeader(errorHeader);
        setModalErrorBody(errorBody);
    };

    const isSomeFileLoading = uploadedFiles.some(
        file => file.status === UploadFileStatuses.new || file.status === UploadFileStatuses.progress
    );
    const isButtonLoadDisabled = loading || !Boolean(getDriveUrls().length) || isSomeFileLoading;

    const isHaveValidAndInvalidFile =
        uploadedFiles.some(item => item.status === UploadFileStatuses.error) &&
        uploadedFiles.some(item => item.status === UploadFileStatuses.success);

    const confirmMerge = () => {
        setStep(PatchFufSteps.SelectFiles);
        runFloatingAsyncAction(addMoreUsersFilesToDrive);
    };

    if (step === PatchFufSteps.ConfirmMerge) {
        return (
            <MergeNotice
                onClose={onClose}
                onSubmit={confirmMerge}
                headerText="Загрузить таблицу из ЕФС-1?"
                bodyText={`Новые данные ${
                    modalSettings?.isOnlyReplace ? "заменят данные в разделе" : `будут объединены с\u00A0текущими`
                }`}
            />
        );
    }

    return (
        <Modal width={566} ignoreBackgroundClick onClose={onClose}>
            <Modal.Header>{modalHeader}</Modal.Header>
            <Modal.Body>
                <ModalLoader loading={loading}>
                    <>
                        <StepPage visible={step === PatchFufSteps.SelectFiles}>{filesContainer}</StepPage>
                        <StepPage visible={step === PatchFufSteps.ErrorMessage}>{modalErrorBody}</StepPage>
                    </>
                </ModalLoader>
            </Modal.Body>
            <Modal.Footer panel sticky>
                <Gapped gap={16}>
                    <Button
                        disabled={isButtonLoadDisabled}
                        onClick={uploadFilesToPatchFuf}
                        use="primary"
                        size="medium"
                        data-tid={"UploadButton"}>
                        Загрузить файлы {isHaveValidAndInvalidFile && !isButtonLoadDisabled && " без ошибок"}
                    </Button>
                    <Button onClick={onClose} data-tid={"CancelButton"} size="medium">
                        Отменить
                    </Button>
                </Gapped>
            </Modal.Footer>
        </Modal>
    );
};
